/**
 * A call. Story V2-091, descended from VC-004 and VC-016.
 *
 * The same conversation as `Chat`, with a live call under it. One session,
 * two input modalities, one history (`TASKS.md` §3) — so the thread, the
 * renderer and the composer are the chat screen's, unchanged, and this file
 * adds only what a call has that a chat does not: two meters and a way to
 * hang up.
 *
 * Nothing here is session state. `voice_server_lld_v2.md` §19.4 has the core
 * derive every field of what is rendered below, and `noState.test.mjs` is
 * the grep that keeps it that way.
 *
 * The transcripts are the exception, and they come from the core: what was
 * *said* reaches the conversation on the server, and the client's own view
 * of a turn in progress is the core's.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { closeSession } from "../api/runtime.js";
import { Equalizer } from "../components/Equalizer.jsx";
import { Icon } from "../components/Icon.jsx";
import { Thread } from "../components/Thread.jsx";
import { rememberSession } from "../session/store.js";
import { useConversation } from "../session/useConversation.js";
import { useVoiceCall } from "../session/useVoiceCall.js";

/** What the connection is doing, for a human. `null` where it is fine. */
const CONNECTION = {
  connecting: "connecting",
  live: null,
  reconnecting: "reconnecting",
  ended: "ended",
};

/**
 * @param {{ config: any, endpoint: string, sessionId: string,
 *           makeDecoder: any, onHome: () => void,
 *           onInspect: (id: string) => void }} props
 */
export function Call({
  config,
  endpoint,
  sessionId,
  makeDecoder,
  onHome,
  onInspect,
}) {
  // The socket's URL and the credential, and nothing else this screen has:
  // `useVoiceCall` takes what it needs rather than the whole configuration,
  // so a new field on `config` cannot quietly reach the core.
  const { state, ended, problem, levels, hangUp, say, setMuted } = useVoiceCall(
    { endpoint, token: config.token },
    { sessionId },
  );
  const { turns, busy, error, send } = useConversation({
    config,
    sessionId,
    makeDecoder,
  });
  const scroll = useRef(null);
  const [draft, setDraft] = useState("");

  const connection = state?.connection?.kind ?? "connecting";
  const live = state?.session === "live" && connection === "live";
  // From the core, never from a local `useState`: the core owns it, and a
  // screen holding its own copy is the defect §19.4 warns about.
  const muted = state?.muted === true;
  const notice = problem ?? CONNECTION[connection];

  // Transcripts are *not* rendered, and that is the design rather than an
  // omission. `voice_agent_hld_v2.md` §12.3: what the provider transcribed
  // is the brain's input, and the record of what the agent said is the
  // brain's own response — "never recorded: 'the agent said X' derived from
  // what the provider generated". A screen that shows both shows the same
  // turn twice, in two wordings, because the thread below already has the
  // conversation the runtime keeps.
  //
  // `voice_server_lld_v2.md` §19.4 says it from the other side: a client
  // that wants history asks the runtime, and a second copy on the client is
  // a second copy to be wrong. This screen used to build exactly that.

  // Written as soon as there is a session rather than at the end: a call
  // that drops is a call somebody wants to get back to.
  useEffect(() => {
    rememberSession(sessionId, { kind: "call", agent: config.agent });
  }, [config.agent, sessionId]);

  useEffect(() => {
    const element = scroll.current;
    if (!element) {
      return;
    }
    const atBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 120;
    if (atBottom) {
      element.scrollTop = element.scrollHeight;
    }
  }, [turns]);

  const type = useCallback(
    (text) => {
      // Over the *socket*, not the HTTP door: §9.3's `input.text` carries a
      // `client_message_id` the core owns, so a resume does not send it
      // twice. `send` is the chat path and is what this screen falls back
      // to once the call is over.
      if (live) {
        // No local echo: §9.3's `input.text` reaches the same conversation
        // the chat door writes to, so the runtime's own record of it comes
        // back down the thread. Echoing here would show it twice.
        say(text);
      } else {
        void send(text);
      }
    },
    [live, say, send],
  );

  const end = useCallback(async () => {
    // The devices first, and the socket with them: a user who pressed the
    // red button expects the microphone light to go out now, not after a
    // round trip. §40.7's close is the runtime's half and follows.
    hangUp();
    try {
      await closeSession(config, sessionId);
      rememberSession(sessionId, { ended: true });
    } catch {
      // Already gone, or unreachable. Going home either way, because the
      // call is over as far as the person who ended it is concerned.
    }
    onHome();
  }, [config, hangUp, onHome, sessionId]);

  return (
    <div className="call">
      <header className="appbar">
        <button
          type="button"
          className="icon-btn"
          onClick={onHome}
          aria-label="Back, leaving the call running"
        >
          <Icon name="back" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="appbar__title">Call AI Agent</h1>
          <div className="appbar__subtitle">
            <span
              className={`dot ${
                problem
                  ? "dot--error"
                  : busy
                    ? "dot--working"
                    : live
                      ? "dot--live"
                      : ""
              }`}
              style={{ display: "inline-block", marginRight: "0.375rem" }}
            />
            {notice ?? "live"}
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={() => onInspect(sessionId)}
          aria-label="What happened inside this session"
          title="What happened inside this session"
        >
          <Icon name="pulse" size={20} />
        </button>
      </header>

      <div className="call__stage">
        <div className="call__meters">
          <Equalizer
            analyser={levels?.output}
            label="agent"
            tone="primary"
            active={live}
          />
          <Equalizer
            analyser={levels?.input}
            label={muted ? "you · muted" : "you"}
            tone="tertiary"
            active={live && !muted}
          />
        </div>

        <div className="convo__scroll" ref={scroll}>
          <Thread
            turns={turns}
            onChoose={type}
            busy={busy}
            emptyMessage={
              live
                ? "Say something. You can interrupt the agent at any time."
                : "Connecting the call…"
            }
          />
          {problem || error ? (
            <p className="status status--error" role="alert">
              {problem ?? error}
            </p>
          ) : null}
        </div>

        {/* A call takes typed input too: one session, two input modalities,
            one conversation (`TASKS.md` §3). The box does not know which
            kind of session it is in, which is the point. */}
        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim()) {
              type(draft);
              setDraft("");
            }
          }}
        >
          <div className="composer__field">
            <textarea
              className="composer__input"
              rows={1}
              value={draft}
              placeholder="…or type instead"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (draft.trim()) {
                    type(draft);
                    setDraft("");
                  }
                }
              }}
            />
            <button
              type="submit"
              className="icon-btn composer__send"
              disabled={!draft.trim()}
              aria-label="Send"
            >
              <Icon name="send" size={20} />
            </button>
          </div>
        </form>
      </div>

      <div className="call__controls">
        {/* A privacy control, so it is next to the one that ends the call
            and not behind a menu. `aria-pressed` and a label that says
            which way it goes: "mute" on a button that is already muted is
            the oldest ambiguity in this widget. */}
        <button
          type="button"
          className={`btn ${muted ? "btn--muted" : ""}`}
          onClick={() => setMuted(!muted)}
          disabled={!live}
          aria-pressed={muted}
          aria-label={muted ? "Unmute your microphone" : "Mute your microphone"}
        >
          <Icon name={muted ? "micOff" : "mic"} size={20} />
          {muted ? "Unmute" : "Mute"}
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={ended ? onHome : end}
        >
          <Icon name="hangup" size={20} />
          {ended ? "Back" : "Hang up"}
        </button>
      </div>
    </div>
  );
}
